import { motion } from "framer-motion";
import { useTilt, Glare } from "@/components/marketing/Depth4D";
import { SectionHeading } from "@/components/marketing/SectionHeading";
import { X, Check, Layers, Boxes } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const OLD_WAY = [
  "Multiple subscriptions",
  "Multiple logins",
  "Separate, siloed systems",
  "Disconnected operational data",
  "Manual reporting",
  "Multiple vendors to manage",
];

const NEW_WAY = [
  "One subscription",
  "One login",
  "One intelligent platform",
  "Connected operational data",
  "ARI-powered business insights",
  "Centralised workforce & operations",
];

export const WhyChooseUs = () => {
  const tOld = useTilt(4), tNew = useTilt(4);
  return (
  <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14" data-testid="why-choose-us">
    <motion.div className="max-w-2xl mb-8 sm:mb-10"
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}>
      <SectionHeading testId="why-heading" eyebrow="Why venues switch" lead="Five tools out." accent="One intelligence in." />
      <p className="text-white/55 mt-5 leading-relaxed">Most venues stitch together a rota app, a training tool, a payroll system, a messaging app and a spreadsheet. We bring it all into one connected operating system.</p>
    </motion.div>

    <div className="grid md:grid-cols-2 gap-4 sm:gap-6 items-stretch">
      {/* Old way */}
      <motion.div data-testid="compare-old" className="group lux-card p-7 sm:p-9 relative" {...tOld.handlers} style={tOld.style}
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: EASE }}>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-xl bg-white/5 ring-1 ring-white/10 flex items-center justify-center"><Boxes className="w-5 h-5 text-white/40" /></span>
          <p className="text-white/60 font-medium">Traditional hospitality software</p>
        </div>
        <ul className="space-y-3.5">
          {OLD_WAY.map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-white/45">
              <span className="shrink-0 w-5 h-5 rounded-full bg-white/5 flex items-center justify-center mt-0.5"><X className="w-3 h-3 text-white/40" /></span>
              <span className="line-through decoration-white/20">{t}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* New way */}
      <motion.div data-testid="compare-new" className="group lux-card p-7 sm:p-9 relative overflow-hidden ring-1 ring-[#D4AF37]/25" {...tNew.handlers} style={tNew.style}
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}>
        <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
        <Glare glare={tNew.glare} />
        <div className="relative flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 ring-1 ring-[#D4AF37]/40 flex items-center justify-center"><Layers className="w-5 h-5 text-[#D4AF37]" /></span>
          <p className="text-white font-medium">Admin Restaurant Intelligence</p>
        </div>
        <ul className="relative space-y-3.5">
          {NEW_WAY.map((t) => (
            <li key={t} className="flex items-start gap-3 text-sm text-white/85">
              <span className="shrink-0 w-5 h-5 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mt-0.5"><Check className="w-3 h-3 text-[#D4AF37]" /></span>
              {t}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  </section>
);
};
